import { Application } from '@platform/client';
import { OrganizationApplicationStatusEnum } from '@platform/client/schemas';
import { Route, Switch, useLocation } from 'wouter';
import { ApplicationStorageDataKeys, Step } from '../Application';
import { OrgFirstChallengeSelection } from '../organization/challenges/OrgFirstChallengeSelection';
import { OrgSecondChallengeSelection } from '../organization/challenges/OrgSecondChallengeSelection';
import { OrgThirdChallengeSelection } from '../organization/challenges/OrgThirdChallengeSelection';
import { OrgPartnerAssociationSelection } from '../organization/final-details/OrgPartnerAssociationSelection';
import { OrgSubmission } from '../organization/final-details/OrgSubmission';
import { OrgBusinessModelSelection } from '../organization/operations/OrgBusinessModelSelection';
import { OrgCountryOperationsSelection } from '../organization/operations/OrgCountryOperationsSelection';
import { OrgFundingSelection } from '../organization/operations/OrgFundingSelection';
import { OrgImpactTrackingSelection } from '../organization/operations/OrgImpactTrackingSelection';
import { OrgRevenueGenerationSelection } from '../organization/operations/OrgRevenueGenerationSelection';
import { OrgSDGSelection } from '../organization/operations/OrgSDGSelection';
import { OrgDetailsSelection } from '../organization/org-profile/OrgDetailsSelection';
import { OrgLogoUpload } from '../organization/org-profile/OrgLogoUpload';
import { OrgProblemSelection } from '../organization/org-profile/OrgProblemSelection';
import { OrgSolutionSelection } from '../organization/org-profile/OrgSolutionSelection';
import { OrgTypeSelection } from '../organization/org-profile/OrgTypeSelection';
import { OrgWebsiteSelection } from '../organization/org-profile/OrgWebsiteSelection';
import { OrgRepBackgroundSelection } from '../organization/rep-profile/OrgRepBackgroundSelection';
import { OrgRepLocationSelection } from '../organization/rep-profile/OrgRepLocationSelection';
import { OrgRepProfilePicUpload } from '../organization/rep-profile/OrgRepProfilePicUpload';
import { OrganizationApplicationDeactivated } from '../organization/result/OrganizationApplicationDeactivated';
import { OrganizationApplicationRejected } from '../organization/result/OrganizationApplicationRejected';
import { OrganizationApplicationUnderReview } from '../organization/result/OrganizationApplicationUnderReview';

enum OrganizationApplicationSteps {
  NAME_SELECTION = 'NAME_SELECTION',
  WEBSITE_SELECTION = 'WEBSITE_SELECTION',
  TYPE_SELECTION = 'TYPE_SELECTION',
  PROBLEM_SELECTION = 'PROBLEM_SELECTION',
  SOLUTION_SELECTION = 'SOLUTION_SELECTION',
  LOGO_UPLOAD = 'LOGO_UPLOAD',

  REVENUE_SELECTION = 'REVENUE_SELECTION',
  FUNDING_SELECTION = 'FUNDING_SELECTION',
  BUSINESS_MODEL_SELECTION = 'BUSINESS_MODEL_SELECTION',
  ORG_LOCATION_SELECTION = 'ORG_LOCATION_SELECTION',
  SDG_SELECTION = 'SDG_SELECTION',
  IMPACT_TRACKING_SELECTION = 'IMPACT_TRACKING_SELECTION',

  FIRST_CHALLENGE_SELECTION = 'FIRST_CHALLENGE_SELECTION',
  SECOND_CHALLENGE_SELECTION = 'SECOND_CHALLENGE_SELECTION',
  THIRD_CHALLENGE_SELECTION = 'THIRD_CHALLENGE_SELECTION',

  REP_LOCATION_SELECTION = 'REP_LOCATION_SELECTION',
  REP_EXPERIENCE_SELECTION = 'REP_EXPERIENCE_SELECTION',
  REP_PROFILE_PICTURE_UPLOAD = 'REP_PROFILE_PICTURE_UPLOAD',

  PARTNER_SELECTION = 'PARTNER_SELECTION',
  REFERRAL_SELECTION = 'REFERRAL_SELECTION',

  // Temporary steps for P2, these will be directed by the backend after submission
  REVIEW = 'REVIEW',
  REJECTED = 'REJECTED',
}

const OrganizationApplicationStep: Record<OrganizationApplicationSteps, Step> = {
  [OrganizationApplicationSteps.NAME_SELECTION]: {
    progress: 20,
    component: OrgDetailsSelection,
    url: '/',
  },
  [OrganizationApplicationSteps.WEBSITE_SELECTION]: {
    progress: 20,
    component: OrgWebsiteSelection,
    url: '/organization-website-selection',
  },
  [OrganizationApplicationSteps.TYPE_SELECTION]: {
    progress: 20,
    component: OrgTypeSelection,
    url: '/organization-type-selection',
  },
  [OrganizationApplicationSteps.PROBLEM_SELECTION]: {
    progress: 20,
    component: OrgProblemSelection,
    url: '/organization-problem-selection',
  },
  [OrganizationApplicationSteps.SOLUTION_SELECTION]: {
    progress: 20,
    component: OrgSolutionSelection,
    url: '/organization-solution-selection',
  },
  [OrganizationApplicationSteps.LOGO_UPLOAD]: {
    progress: 90,
    component: OrgLogoUpload,
    url: '/organization-logo-upload',
  },
  [OrganizationApplicationSteps.REVENUE_SELECTION]: {
    progress: 120,
    component: OrgRevenueGenerationSelection,
    url: '/organization-revenue-selection',
  },
  [OrganizationApplicationSteps.FUNDING_SELECTION]: {
    progress: 120,
    component: OrgFundingSelection,
    url: '/organization-funding-selection',
  },
  [OrganizationApplicationSteps.BUSINESS_MODEL_SELECTION]: {
    progress: 120,
    component: OrgBusinessModelSelection,
    url: '/organization-business-model-selection',
  },
  [OrganizationApplicationSteps.ORG_LOCATION_SELECTION]: {
    progress: 120,
    component: OrgCountryOperationsSelection,
    url: '/organization-location-selection',
  },
  [OrganizationApplicationSteps.SDG_SELECTION]: {
    progress: 120,
    component: OrgSDGSelection,
    url: '/organization-sdg-selection',
  },
  [OrganizationApplicationSteps.IMPACT_TRACKING_SELECTION]: {
    progress: 120,
    component: OrgImpactTrackingSelection,
    url: '/organization-impact-tracking-selection',
  },
  [OrganizationApplicationSteps.FIRST_CHALLENGE_SELECTION]: {
    progress: 230,
    component: OrgFirstChallengeSelection,
    url: '/organization-first-challenge-selection',
  },
  [OrganizationApplicationSteps.SECOND_CHALLENGE_SELECTION]: {
    progress: 230,
    component: OrgSecondChallengeSelection,
    url: '/organization-second-challenge-selection',
  },
  [OrganizationApplicationSteps.THIRD_CHALLENGE_SELECTION]: {
    progress: 230,
    component: OrgThirdChallengeSelection,
    url: '/organization-third-challenge-selection',
  },
  [OrganizationApplicationSteps.REP_LOCATION_SELECTION]: {
    progress: 330,
    component: OrgRepLocationSelection,
    url: '/organization-rep-location-selection',
  },
  [OrganizationApplicationSteps.REP_EXPERIENCE_SELECTION]: {
    progress: 330,
    component: OrgRepBackgroundSelection,
    url: '/organization-rep-experience-selection',
  },
  [OrganizationApplicationSteps.REP_PROFILE_PICTURE_UPLOAD]: {
    progress: 330,
    component: OrgRepProfilePicUpload,
    url: '/organization-rep-profile-picture-upload',
  },
  [OrganizationApplicationSteps.PARTNER_SELECTION]: {
    progress: 440,
    component: OrgPartnerAssociationSelection,
    url: '/organization-partner-selection',
  },
  [OrganizationApplicationSteps.REFERRAL_SELECTION]: {
    progress: 440,
    component: OrgSubmission,
    url: '/organization-referral-selection',
  },

  /**
   * Temporary, these will be directed by the backend after submission in P2
   */

  [OrganizationApplicationSteps.REVIEW]: {
    progress: 100,
    component: OrganizationApplicationUnderReview,
    url: '/organization-review',
  },
  [OrganizationApplicationSteps.REJECTED]: {
    progress: 100,
    component: OrganizationApplicationRejected,
    url: '/organization-rejected',
  },
} as const;

export const OrganizationApplicationRoot = () => {
  const [location, navigate] = useLocation();

  const goToNextStep = () => {
    const steps = Object.values(OrganizationApplicationSteps);
    const currentIndex = steps.findIndex((step) => {
      return OrganizationApplicationStep[step as OrganizationApplicationSteps].url === location;
    });
    const nextStep =
      currentIndex < steps.length - 1 ? steps[currentIndex + 1] : steps[currentIndex];
    const nextUrl = OrganizationApplicationStep[nextStep as OrganizationApplicationSteps].url;
    navigate(nextUrl);
  };

  const goToPreviousStep = () => {
    const steps = Object.values(OrganizationApplicationSteps);
    const currentIndex = steps.findIndex(
      (step) => OrganizationApplicationStep[step as OrganizationApplicationSteps].url === location,
    );

    if (currentIndex === 0) {
      window.location.href = 'platform/auth/application-start?is_rep=true';
      return;
    }

    const previousStep = steps[currentIndex - 1];
    const previousUrl =
      OrganizationApplicationStep[previousStep as OrganizationApplicationSteps].url;

    navigate(previousUrl);
  };

  const applicationData = Application.useGetOrganizationApplicationStatus();

  if (applicationData.data?.applicationStatus === OrganizationApplicationStatusEnum.rejected) {
    return <OrganizationApplicationRejected />;
  }

  if (applicationData.data?.applicationStatus === OrganizationApplicationStatusEnum.deactivated) {
    return <OrganizationApplicationDeactivated />;
  }

  if (
    applicationData.data?.applicationStatus === OrganizationApplicationStatusEnum.pending ||
    applicationData.data?.applicationStatus === OrganizationApplicationStatusEnum.info_requested
  ) {
    return <OrganizationApplicationUnderReview />;
  }

  return (
    <Switch>
      {Object.entries(OrganizationApplicationStep).map(([_key, value]) => {
        const { url, component: Component, progress } = value;
        return (
          <Route key={url} path={url}>
            <Component
              onBack={goToPreviousStep}
              onContinue={goToNextStep}
              progress={progress}
              dataKey={ApplicationStorageDataKeys.ORGANIZATION_APPLICATION}
            />
          </Route>
        );
      })}
    </Switch>
  );
};
